<template>
  <div class="settings-wrapper">
    <SettingsCardProfile
      :user="user"
      :loading="isProfileSettingsLoading"
      :initial-form-data="photographerProfileForm"
      :required-rule="required"
      :validate-instagram-rule="validateInstagram"
      @save="savePhotographerProfileSettings"
    />

    <SettingsCardGlobal
      v-if="user"
      :user="user"
      :initial-form-data="photoSalesForm"
      :loading="isGlobalSettingsLoading"
      :required-rule="required"
      :min-price-rule="minPrice"
      @save="saveGlobalSettings"
    />

    <SettingsCardEvents
      :events="paginatedEvents"
      :total-events="eventsTotalCount"
      :loading="isEventListLoading"
      :cities-list="citiesList"
      :required-rule="required"
      :min-price-rule="minPrice"
      :fetch-gears="changePage"
      @save-event="saveEventSettings"
      @toggle-edit="toggleEdit"
    />
  </div>
</template>

<script>
import { Notify } from 'quasar';
import { mapActions, mapState } from 'vuex';

import SettingsCardGlobal from './SettingsCardGlobal.vue';
import SettingsCardProfile from './SettingsCardProfile.vue';
import SettingsCardEvents from './SettingsCardEvents.vue';

export default {
  name: 'SettingsCardWrapper',
  components: {
    SettingsCardGlobal,
    SettingsCardProfile,
    SettingsCardEvents,
  },
  data() {
    return {
      isGlobalSettingsLoading: false,
      isProfileSettingsLoading: false,
      eventsWithSettings: [],
      currentPage: 1,
      perPage: 6,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      eventsWithConfigurablePricesList: (state) => state.global.eventsWithConfigurablePricesList || [],
      eventsTotalCount: state => state.global.eventsTotalCount || 0,
      citiesList: (state) => state.global.citiesList,
      isEventListLoading: (state) => state.global.isEventListLoading,
    }),
    paginatedEvents() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.eventsWithSettings.slice(start, start + this.perPage);
    },
    photoSalesForm() {
      if (!this.user) {
        return { disableBulkSale: false, singlePhotoPrice: 0 };
      }

      return {
        disableBulkSale: this.user.disable_global_bulk_sale || false,
        singlePhotoPrice: this.user.global_photo_price || 0,
      };
    },
    photographerProfileForm() {
      if (!this.user) {
        return { headline: '', bio: '', instagram: '', region: '', openForBookings: false};
      }
      return {
        headline: this.user.photographer_headline || '',
        bio: this.user.photographer_bio || '',
        instagram: this.user.photographer_instagram_username || '',
        region: this.user.photographer_region || '',
        openForBookings: this.user.open_for_bookings || false,
      };
    },
    normalizedEventSalesSettings() {
      const sales = this.user && this.user.event_sales;
      if (!sales) {
        return {};
      }
      if (Array.isArray(sales)) {
        return sales.reduce((acc, entry) => {
          acc[entry.eventId] = entry;
          return acc;
        }, {});
      }
      return sales;
    },
    defaultEventSalesSettings() {
      return {
        disableBulkSale: this.user ? (this.user.disable_global_bulk_sale || false) : false,
        singlePhotoPrice: this.user ? (this.user.global_photo_price || 0) : 0,
      };
    },
  },
  methods: {
    ...mapActions('global', ['getEvents', 'getCities']),
    ...mapActions('auth', [
      'getUserInfo',
      'updateGlobalSaleSettings',
      'updateEventPhotoSettings',
      'updatePhotographerProfile',
    ]),

    mapUserSettingsToEvent() {
      this.eventsWithSettings = this.eventsWithConfigurablePricesList.map((event) => {
        const existing = this.eventsWithSettings.find(e => e.id === event.id);
        const userEventSettings = this.normalizedEventSalesSettings[event.id] || this.defaultEventSalesSettings;

        return {
          ...event,
          editing: existing ? existing.editing : false,
          saving: existing ? existing.saving : false,
          disableBulkSale: userEventSettings.disableBulkSale || false,
          originalDisableBulkSale: userEventSettings.disableBulkSale || false,
          photoPrice: userEventSettings.singlePhotoPrice || 0,
          locked: userEventSettings.locked || false,
          restored: userEventSettings.restored || false,
        };
      });
    },
    required(val) {
      return (
        (val !== null && val !== undefined && val !== '') ||
        this.$t('validation.required')
      );
    },
    validateInstagram(val) {
      const regex = /^[a-zA-Z0-9._]{1,30}$/;
      return regex.test(val) || this.$t('validation.invalidFormat');
    },
    minPrice(min) {
      return (val) => val >= min || this.$t('validation.minPrice', { min });
    },
    showNotification(message, type) {
      Notify.create({
        message: this.$t(message),
        type,
        position: 'bottom',
        timeout: 3000,
      });
    },
    toggleEdit(eventId) {
      const event = this.eventsWithSettings.find((e) => e.id === eventId);
      if (event) {
        event.editing = !event.editing;
        if (!event.editing) {
          const userEventSettings = this.normalizedEventSalesSettings[event.id] || this.defaultEventSalesSettings;
          event.disableBulkSale = userEventSettings.disableBulkSale || false;
          event.photoPrice = userEventSettings.singlePhotoPrice || 0;
        }
      }
    },
    initializeFromUserData() {
      if (this.user) {
        this.photoSalesForm.disableBulkSale = this.user.disable_global_bulk_sale || false;
        this.photoSalesForm.singlePhotoPrice = this.user.global_photo_price || 0;

        this.photographerProfileForm.headline = this.user.photographer_headline || '';
        this.photographerProfileForm.bio = this.user.photographer_bio || '';
        this.photographerProfileForm.instagram = this.user.photographer_instagram_username || '';
        this.photographerProfileForm.region = this.user.photographer_region || '';
        this.photographerProfileForm.openForBookings = this.user.open_for_bookings || false;
      }
    },
    async saveGlobalSettings(formData) {
      this.isGlobalSettingsLoading = true;
      try {
        await this.updateGlobalSaleSettings({
          disableBulkSale: formData.disableBulkSale,
          globalSinglePhotoPrice: formData.singlePhotoPrice
        });
        this.showNotification('userPanel.settings.success', 'positive');
      } catch (error) {
        this.showNotification('userPanel.settings.error', 'negative');
      } finally {
        this.isGlobalSettingsLoading = false;
      }
    },
    async savePhotographerProfileSettings(formData) {
      this.isProfileSettingsLoading = true;
      try {
        await this.updatePhotographerProfile({
          photographerHeadline: formData.headline,
          photographerBio: formData.bio,
          photographerInstagram: formData.instagram,
          photographerRegion: formData.region,
          openForBookings: formData.openForBookings,
        });
        this.showNotification('userPanel.settings.success', 'positive');
      } catch (error) {
        this.showNotification('userPanel.settings.error', 'negative');
      } finally {
        this.isProfileSettingsLoading = false;
      }
    },
    async saveEventSettings({ eventId, disableBulkSale, photoPrice }) {
      this.setEventState(eventId, { saving: true });

      try {
        await this.updateEventPhotoSettings({
          eventId: eventId,
          disableBulkSale: disableBulkSale,
          singlePhotoPrice: photoPrice || 0,
        });
        this.showNotification('userPanel.settings.success', 'positive');
        this.setEventState(eventId, { editing: false });
      } catch (error) {
        this.showNotification('userPanel.settings.error', 'negative');
      } finally {
       this.setEventState(eventId, { saving: false });
      }
    },
    setEventState(id, stateChanges) {
      const eventToUpdate = this.eventsWithSettings.find((e) => e.id === id);
      if (eventToUpdate) {
        Object.assign(eventToUpdate, stateChanges);
      }
    },
    changePage({ page = 1 } = {}) {
      this.currentPage = page;
    }
  },
  watch: {
    eventsWithConfigurablePricesList: {
      handler() {
        this.mapUserSettingsToEvent();
      },
      immediate: true,
    },
    user: {
      handler() {
        this.mapUserSettingsToEvent();
      },
      deep: true,
    },
  },
  async created() {
    await this.getUserInfo();
    await this.getCities();
    await this.getEvents();
    this.initializeFromUserData();
  },
};
</script>

<style lang="scss" scoped>
.settings {
  &-wrapper {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2.5rem;
  }
}

@media screen and (min-width: $breakpoint-md-min) {
  .settings {
    &-wrapper {
      padding: 1.5rem 2.5rem;
      gap: 3rem;
    }
  }
}
</style>
